import React, { useState, useEffect } from 'react';
import accountService from '../services/accountService';
import { formatError } from '../utils/errorHelper';
import ColorPicker from '../components/ColorPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import { MdEdit, MdDelete } from 'react-icons/md';
import './Accounts.css';

function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    initial_balance: '',
    type: 'checking',
    color: '#10243C',
    description: '',
    notes: '',
    is_active: true,
  });
  const [notifications, setNotifications] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning',
    confirmText: 'Confirmar',
    cancelText: 'Cancelar'
  });

  const accountTypes = {
    checking: 'Conta Corrente',
    savings: 'Poupança',
    investment: 'Investimento',
    cash: 'Dinheiro',
    credit_card: 'Cartão de Crédito',
    other: 'Outro',
  };

  useEffect(() => {
    loadAccounts();
  }, []);

  // Função para adicionar notificação
  const addNotification = (type, title, message, duration = 5000) => {
    const id = Date.now();
    const notification = { id, type, title, message };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remover após duration
    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id);
      }, duration);
    }
  };

  // Função para remover notificação
  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const data = await accountService.getAccounts();
      
      // Carregar saldo de cada conta
      const accountsWithBalance = await Promise.all(
        (data.data || data).map(async (account) => {
          try {
            const balanceData = await accountService.getAccountBalance(account.id);
            return {
              ...account,
              current_balance: balanceData.data?.balance || balanceData.balance || 0,
            };
          } catch (error) {
            console.error(`Erro ao carregar saldo da conta ${account.id}:`, error);
            return {
              ...account,
              current_balance: 0,
            };
          }
        })
      );

      setAccounts(accountsWithBalance);
    } catch (error) {
      console.error('Erro ao carregar contas:', error);
      addNotification('error', 'Erro ao Carregar', 'Erro ao carregar contas. Verifique sua conexão.', 6000);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        initial_balance: parseFloat(formData.initial_balance) || 0,
      };

      if (editingAccount) {
        await accountService.updateAccount(editingAccount.id, dataToSend);
      } else {
        await accountService.createAccount(dataToSend);
      }

      addNotification('success', 'Sucesso', editingAccount ? 'Conta atualizada com sucesso!' : 'Conta criada com sucesso!', 3000);
      setShowForm(false);
      setEditingAccount(null);
      resetForm();
      loadAccounts();
    } catch (error) {
      console.error('Erro ao salvar conta:', error);
      addNotification('error', 'Erro ao Salvar', formatError(error, 'Erro ao salvar conta. Verifique os dados.'), 6000);
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      initial_balance: account.initial_balance.toString(),
      type: account.type,
      color: account.color || '#10243C',
      description: account.description || '',
      notes: account.notes || '',
      is_active: account.is_active,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Excluir Conta',
      message: 'Deseja realmente excluir esta conta?\n\nAtenção: Se a conta estiver sendo usada em transações, ela não poderá ser excluída.\n\nEsta ação não pode ser desfeita.',
      type: 'error',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          await accountService.deleteAccount(id);
          addNotification('success', 'Sucesso', 'Conta excluída com sucesso!', 3000);
          loadAccounts();
        } catch (error) {
          console.error('Erro ao excluir conta:', error);
          addNotification('error', 'Erro ao Excluir', formatError(error, 'Erro ao excluir conta. Ela pode estar sendo usada em transações.'), 6000);
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      initial_balance: '',
      type: 'checking',
      color: '#10243C',
      description: '',
      notes: '',
      is_active: true,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getTotalBalance = () => {
    return accounts.reduce((sum, account) => {
      if (account.is_active) {
        return sum + (account.current_balance || 0);
      }
      return sum;
    }, 0);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="accounts-page">
      <div className="page-header">
        <h1>Contas</h1>
        <button className="btn-primary" onClick={() => setShowForm(true)}>
          + Nova Conta
        </button>
      </div>

      <div className="total-balance-card">
        <h3>Saldo Total</h3>
        <p className="total-amount">{formatCurrency(getTotalBalance())}</p>
        <p className="accounts-count">{accounts.filter(a => a.is_active).length} contas ativas</p>
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingAccount ? 'Editar' : 'Nova'} Conta</h2>
              <button
                className="close-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditingAccount(null);
                  resetForm();
                }}
              >
                ✕
              </button>
            </div>
            <form className="account-form" onSubmit={handleSubmit}>
              <div className="account-form-body">
                <div className="form-group">
                  <label>Nome da Conta</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    placeholder="Ex: Banco do Brasil - Conta Corrente"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Tipo</label>
                    <select name="type" value={formData.type} onChange={handleFormChange}>
                      {Object.entries(accountTypes).map(([key, label]) => (
                        <option key={key} value={key}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Saldo Inicial</label>
                    <input
                      type="number"
                      step="0.01"
                      name="initial_balance"
                      value={formData.initial_balance}
                      onChange={handleFormChange}
                      required
                      placeholder="0,00"
                    />
                  </div>
                </div>

                <ColorPicker
                  value={formData.color}
                  onChange={(color) => setFormData({ ...formData, color })}
                  label="Cor"
                />

                <div className="form-group">
                  <label>Descrição</label>
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    placeholder="Descrição breve da conta"
                  />
                </div>

                <div className="form-group">
                  <label>Observações</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows="3"
                    placeholder="Observações adicionais"
                  />
                </div>

                <div className="form-group-checkbox">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleFormChange}
                  />
                  <label htmlFor="is_active">Conta Ativa</label>
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowForm(false);
                    setEditingAccount(null);
                    resetForm();
                  }}
                >
                  Cancelar
                </button>
                <button type="submit" className="btn-primary">
                  {editingAccount ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="accounts-grid">
        {accounts.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma conta cadastrada.</p>
          </div>
        ) : (
          accounts.map((account) => (
            <div key={account.id} className="account-card">
              <div className="account-header">
                <div
                  className="account-color"
                  style={{ backgroundColor: account.color }}
                />
                <span className="account-type">{accountTypes[account.type]}</span>
              </div>
              <h3>{account.name}</h3>
              {account.description && (
                <p className="account-description">{account.description}</p>
              )}
              <div className="account-balance-section">
                <div className="balance-item">
                  <span className="balance-label">Saldo Inicial</span>
                  <span className="balance-value">
                    {formatCurrency(account.initial_balance)}
                  </span>
                </div>
                <div className="balance-item">
                  <span className="balance-label">Saldo Atual</span>
                  <span className="balance-value balance-current">
                    {formatCurrency(account.current_balance || 0)}
                  </span>
                </div>
              </div>
              <div className="account-footer">
                <span
                  className={`status-badge ${account.is_active ? 'active' : 'inactive'}`}
                >
                  {account.is_active ? 'Ativa' : 'Inativa'}
                </span>
                <div className="account-actions">
                  <button className="action-btn edit" onClick={() => handleEdit(account)} title="Editar">
                    <MdEdit />
                  </button>
                  <button className="action-btn delete" onClick={() => handleDelete(account.id)} title="Excluir">
                    <MdDelete />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Toast Notifications */}
      <div className="toast-container">
        {notifications.map((notification) => (
          <div key={notification.id} className={`toast ${notification.type}`}>
            <div className="toast-icon">
              {notification.type === 'success' && '✓'}
              {notification.type === 'error' && '✕'}
              {notification.type === 'warning' && '⚠'}
              {notification.type === 'info' && 'ℹ'}
            </div>
            <div className="toast-content">
              <h4 className="toast-title">{notification.title}</h4>
              <p className="toast-message">{notification.message}</p>
            </div>
            <button 
              className="toast-close" 
              onClick={() => removeNotification(notification.id)}
              aria-label="Fechar"
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText={confirmDialog.confirmText}
        cancelText={confirmDialog.cancelText}
      />
    </div>
  );
}

export default Accounts;
